import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Search, Star } from "lucide-react";
import api from "@/lib/api";
import Seo, { breadcrumbJsonLd, itemListJsonLd, speakableJsonLd } from "@/components/Seo";
import StreakBoostBanner from "@/components/StreakBoostBanner";
import { AddressLink } from "@/components/ContactLinks";
import { resolveImgSrc } from "@/components/ImageUpload";

// Leaflet, react-leaflet and Leaflet CSS are heavy (~150 KB gzipped). Load
// them in a separate async chunk that only ships when the user opens the map.
const DirectoryMap = lazy(() => import("@/pages/DirectoryMap"));

const CATS = ["All","Restaurants","Sports Bars","Breweries","Food Trucks","Hotels","Live Music","Family Friendly","Deals","Coupons","Official Watch Party Locations"];

export default function Directory() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [wpOnly, setWpOnly] = useState(false);
  const [view, setView] = useState("list");
  // Lazy-mount: only build the map once the user first opens the Map tab.
  // From then on, it stays mounted (hidden via CSS) to avoid the react-leaflet
  // "Map container is already initialized" error under React StrictMode.
  const [mapMounted, setMapMounted] = useState(false);
  useEffect(() => { if (view === "map") setMapMounted(true); }, [view]);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (cat !== "All") params.set("category", cat);
    if (wpOnly) params.set("watch_party_only", "true");
    api.get(`/businesses?${params.toString()}`).then(r => setItems(r.data));
  };
  useEffect(load, [q, cat, wpOnly]);

  const mapped = useMemo(() => items.filter(i => i.latitude && i.longitude), [items]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <Seo
        title="Business Directory — Jacksonville Watch Party Partners"
        description="Browse Jacksonville's Official Watch Party Partners, sports bars, breweries, restaurants, food trucks, and hotels participating in ADN Local FanFest 2026. Filter by category or view on the map."
        keywords="Jacksonville sports bars, Jacksonville breweries, watch party Jacksonville, ADN FanFest partners, Jacksonville restaurants World Cup"
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Home", url: "/" },
            { name: "Business Directory", url: "/directory" },
          ]),
          itemListJsonLd(
            "Jacksonville Watch Party Partners — ADN Local FanFest",
            items.slice(0, 50).map(b => ({
              name: b.business_name,
              url: `/business/${b.id}`,
              description: b.description,
              image: (b.photos && b.photos[0]) || undefined,
            })),
          ),
          speakableJsonLd([".speakable-title", ".speakable-summary"]),
        ]}
      />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-primary">Local Discovery</div>
          <h1 className="font-bebas text-5xl mt-2 speakable-title">BUSINESS DIRECTORY</h1>
          <p className="sr-only speakable-summary">
            Browse Jacksonville&apos;s Official Watch Party Partners for the 2026 ADN Local FanFest — sports bars, breweries, restaurants, food trucks, and hotels. Check in at any partner between July 6 and July 19, 2026 to earn Fan Passport stamps and qualify for the Grand Prize Cancun trip.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={()=>setView("list")}
            data-testid="view-list"
            className={`pill-btn ${view==="list"?"active":""}`}>List</button>
          <button onClick={()=>setView("map")}
            data-testid="view-map"
            className={`pill-btn ${view==="map"?"active":""}`}>Map</button>
        </div>
      </div>

      {/* Sponsor-boosted Streak Multiplier banner — hidden when no active boosts */}
      <div className="mt-5">
        <StreakBoostBanner />
      </div>

      {/* Search + Filters */}
      <div className="mt-6 adn-card p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-2 flex-1 bg-[#0b1428] border border-adn-border rounded px-3 py-2">
          <Search size={16} className="text-white/50" />
          <input placeholder="Search restaurants, bars, breweries…" value={q} onChange={e=>setQ(e.target.value)}
            data-testid="directory-search"
            className="bg-transparent flex-1 outline-none text-sm placeholder:text-white/40" />
        </div>
        <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60">
          <input type="checkbox" checked={wpOnly} onChange={e=>setWpOnly(e.target.checked)} className="accent-primary"
            data-testid="filter-watchparty"/> Watch Party only
        </label>
      </div>
      <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
        {CATS.map(c => (
          <button key={c} onClick={()=>setCat(c)}
            data-testid={`cat-${c}`}
            className={`pill-btn shrink-0 ${cat===c?"active":""}`}>
            {c}
          </button>
        ))}
      </div>

      {/* Map is always mounted once first shown, then hidden via CSS to avoid
          the react-leaflet 'Map container is already initialized' error under
          StrictMode double-invoke. */}
      {mapMounted && (
        <div className={`mt-6 h-[560px] rounded overflow-hidden border border-adn-border ${view === "map" ? "block" : "hidden"}`}
          data-testid="directory-map-wrap">
          <Suspense fallback={
            <div className="w-full h-full flex items-center justify-center text-white/50 text-sm">Loading map…</div>
          }>
            <DirectoryMap mapped={mapped} visible={view === "map"} />
          </Suspense>
        </div>
      )}
      {view === "list" && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map(b => (
            <Link key={b.id} to={`/business/${b.id}`}
              data-testid={`biz-card-${b.id}`}
              className="adn-card adn-card-hover overflow-hidden group">
              <div className="h-40 overflow-hidden">
                <img src={resolveImgSrc(b.photos && b.photos[0]) || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800"}
                     alt={`${b.business_name} — ${(b.categories || []).join(", ")} in Jacksonville, FL`}
                     loading="lazy"
                     className="w-full h-full object-cover group-hover:scale-105 transition"/>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase">
                  {b.sponsor_tier !== "free" && <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary flex items-center gap-1"><Star size={10}/> {b.sponsor_tier}</span>}
                  {b.watch_party_partner && <span className="px-2 py-0.5 rounded-full bg-secondary/20 text-secondary">Watch Party</span>}
                </div>
                <div className="font-bebas text-2xl mt-2">{b.business_name}</div>
                <div className="text-xs text-white/60 mt-1 flex items-center gap-1" onClick={(e)=>e.stopPropagation()}>
                  <MapPin size={12}/>
                  <AddressLink address={b.address} city={b.city} state={b.state} className="hover:text-primary transition-colors">{b.address}</AddressLink>
                </div>
                <div className="text-xs text-white/60 mt-2 line-clamp-2">{b.description}</div>
              </div>
            </Link>
          ))}
          {items.length === 0 && (
            <div className="col-span-full text-center text-white/50 py-16">No businesses match your filters.</div>
          )}
        </div>
      )}
    </div>
  );
}
